import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { coursService } from '../../api';
import { 
  FiSearch, 
  FiFilter, 
  FiBookOpen, 
  FiClock, 
  FiUser, 
  FiStar, 
  FiChevronRight,
  FiPlusCircle,
  FiGrid
} from 'react-icons/fi';
import Header from '../Header/Header';
import Footer from '../footer/Footer';
import './CoursList.css';

const CoursList = () => {
  const [cours, setCours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    niveau: '',
    gratuit: false,
    sous_service_id: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCours();
  }, []);

  const fetchCours = async () => {
    setLoading(true);
    try {
      const params = {
        search: searchTerm || undefined,
        niveau: filters.niveau || undefined,
        gratuit: filters.gratuit ? 1 : undefined,
        sous_service_id: filters.sous_service_id || undefined
      };

      const response = await coursService.getAll(params);
      
      console.log('CoursList - Response:', response);
      
      if (response && response.data) {
        const coursList = response.data.data || [];
        setCours(coursList);
      } else {
        setCours([]);
      }
    } catch (err) {
      console.error('Erreur chargement cours:', err);
      setError('Impossible de charger les cours. Veuillez réessayer.');
      setCours([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCours();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchCours();
  };

  const resetFilters = () => {
    setFilters({ niveau: '', gratuit: false, sous_service_id: '' });
    setSearchTerm('');
    setTimeout(fetchCours, 100);
  };

  const formatPrice = (price) => {
    if (price === 0 || price === null || price === undefined) return 'Gratuit';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const getNiveauBadge = (niveau) => {
    const colors = {
      'débutant': 'badge-debutant',
      'intermédiaire': 'badge-intermediaire',
      'avancé': 'badge-avance'
    };
    return colors[niveau] || 'badge-debutant';
  };

  const getStatutBadge = (statut) => {
    const colors = {
      'brouillon': 'badge-brouillon',
      'publié': 'badge-publie',
      'archivé': 'badge-archive'
    };
    return colors[statut] || 'badge-brouillon';
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isMentor = user?.role === 'mentor';

  if (loading) {
    return (
      <div className="page-wrapper">
        <Header />
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Chargement des cours...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <Header />
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchCours} className="btn-retry">Réessayer</button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <Header />
      
      <div className="main-container">
        <div className="content-wrapper">
          <div className="header-section">
            <div className="header-left">
              <h2 className="section-title">Cours en ligne</h2>
              <span className="cours-count">{cours.length} formation{cours.length > 1 ? 's' : ''}</span>
            </div>
            {isMentor && (
              <Link to="/cours/ajouter" className="btn-create">
                <FiPlusCircle /> Créer un cours
              </Link>
            )}
          </div>

          {/* Barre de recherche */}
          <div className="search-section">
            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input-wrap">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Rechercher un cours..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              <button type="submit" className="btn-search">Rechercher</button>
              <button 
                type="button" 
                className="btn-filter-toggle"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FiFilter /> {showFilters ? 'Masquer' : 'Filtres'}
              </button>
            </form>
          </div>

          {/* Filtres */}
          {showFilters && (
            <div className="filters-panel">
              <div className="filter-group">
                <label>Niveau</label>
                <select 
                  value={filters.niveau}
                  onChange={(e) => handleFilterChange('niveau', e.target.value)}
                >
                  <option value="">Tous les niveaux</option>
                  <option value="débutant">Débutant</option>
                  <option value="intermédiaire">Intermédiaire</option>
                  <option value="avancé">Avancé</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Prix</label>
                <label className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={filters.gratuit}
                    onChange={(e) => handleFilterChange('gratuit', e.target.checked)}
                  />
                  Gratuit uniquement
                </label>
              </div>

              <button className="btn-apply-filters" onClick={applyFilters}>
                Appliquer
              </button>

              <button className="btn-clear-filters" onClick={resetFilters}>
                Réinitialiser
              </button>
            </div>
          )}

          {/* Liste des cours */}
          {cours.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h3>Aucun cours disponible</h3>
              <p>Vérifiez vos filtres ou revenez plus tard pour découvrir de nouvelles formations.</p>
              {isMentor && (
                <Link to="/cours/ajouter" className="btn-create-first">
                  <FiPlusCircle /> Créer votre premier cours
                </Link>
              )}
            </div>
          ) : (
            <div className="cours-grid">
              {cours.map((coursItem) => (
                <div key={coursItem.id} className="cours-card">
                  <div className="card-image">
                    <img 
                      src={coursItem.image_url || '/default-cours.jpg'} 
                      alt={coursItem.titre}
                      onError={(e) => { e.target.src = '/default-cours.jpg'; }}
                    />
                    <span className={`card-badge statut ${getStatutBadge(coursItem.statut)}`}>
                      {coursItem.statut}
                    </span>
                    <span className={`card-badge niveau ${getNiveauBadge(coursItem.niveau)}`}>
                      {coursItem.niveau}
                    </span>
                  </div>

                  <div className="card-body">
                    <h3 className="card-title">{coursItem.titre}</h3>
                    <p className="card-description">
                      {coursItem.description?.substring(0, 120)}
                      {coursItem.description?.length > 120 && '...'}
                    </p>

                    <div className="card-meta">
                      <span className="meta-item">
                        <FiUser className="meta-icon" />
                        {coursItem.mentor?.prenom || 'Mentor'}
                      </span>
                      {coursItem.duree_estimee && (
                        <span className="meta-item">
                          <FiClock className="meta-icon" />
                          {Math.floor(coursItem.duree_estimee / 60)}h
                        </span>
                      )}
                      <span className="meta-item">
                        <FiBookOpen className="meta-icon" />
                        {coursItem.lecons?.length || 0} leçons
                      </span>
                    </div>

                    <div className="card-footer">
                      <span className="card-price">{formatPrice(coursItem.prix)}</span>
                      <Link 
                        to={`/cours/${coursItem.id}`} 
                        className="btn-view"
                      >
                        Voir <FiChevronRight />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CoursList;